import { TableBody, TableCell, TableContainer, TableRow } from "@mui/material";
import StyledTable from "components/StyledTable";
import ScheduleProvider, { type ScheduleEntry, ScheduleScale } from "./ScheduleContext";
import ScheduleHeaders from "./ScheduleHeaders";
import ScheduleRow from "./ScheduleRow";
import React, { useMemo } from "react";
import { groupBy } from "utilities/groupBy";
import ScheduleItem, { ScheduleItemProps } from "./ScheduleItem";

type ScheduleViewProps<TRow extends { id: React.Key }, TEntry extends ScheduleEntry> = {
  rows: TRow[];
  entries: TEntry[];
  groupById: (entry: TEntry) => React.Key;
  scale: ScheduleScale;
  fromDate: Date;
  toDate: Date;
  firstColumnHeader: React.ReactNode;
  renderRowHeader: ([row, entries]: [TRow, TEntry[]]) => React.ReactNode;
  showDayNumbers?: boolean;
  entryProps?: (entry: TEntry) => {
    color: ScheduleItemProps["color"];
    state: ScheduleItemProps["state"];
    tooltip: ScheduleItemProps["tooltip"];
  };
} & (
  | {
      enableSelection?: false;
      selectedEntries?: React.Key[];
      setSelectedEntries?: (entryIds: React.Key[]) => void;
    }
  | {
      enableSelection: true;
      selectedEntries: React.Key[];
      setSelectedEntries: (entryIds: React.Key[]) => void;
    }
);

export type ScheduleColumn = { name: string; ratio: number; startDate: Date };

const ScheduleView = <TRow extends { id: React.Key }, TEntry extends ScheduleEntry>(
  props: ScheduleViewProps<TRow, TEntry>,
) => {
  const {
    rows,
    entries,
    scale,
    groupById,
    firstColumnHeader,
    renderRowHeader,
    entryProps,
    enableSelection = false,
    selectedEntries = [],
    setSelectedEntries = () => {},
    showDayNumbers,
  } = props;

  const groupedEntries = useMemo(() => {
    const mappedRows = new Map(rows.map((row) => [row.id, row]));
    const mappedEntries = groupBy(entries, groupById);

    return new Map(
      Array.from(mappedRows.values()).map((row) => [row, mappedEntries.get(row.id) || []]),
    );
  }, [rows, entries, groupById]);

  return (
    <ScheduleProvider
      scale={scale}
      fromDate={props.fromDate}
      toDate={props.toDate}
      entries={entries}
      enableSelection={enableSelection}
      selectedEntries={selectedEntries}
      setSelectedEntries={setSelectedEntries}
      showDayNumbers={showDayNumbers}
    >
      <TableContainer>
        <StyledTable size="small" striped={scale === "MONTHS"}>
          <ScheduleHeaders firstColumnHeader={firstColumnHeader} />

          <TableBody>
            {!entries.length ? (
              <TableRow>
                <TableCell colSpan={100} align="center" sx={{ fontStyle: "italic" }}>
                  Ingen data matchar sökningen
                </TableCell>
              </TableRow>
            ) : (
              Array.from(groupedEntries.entries()).map(([row, entries]) => (
                <ScheduleRow
                  key={row.id}
                  renderTitle={renderRowHeader([row, entries])}
                  rowEntries={entries}
                >
                  {entries.map((entry) => (
                    <ScheduleItem
                      key={entry.id}
                      entry={entry}
                      {...(entryProps ? entryProps(entry) : {})}
                    />
                  ))}
                </ScheduleRow>
              ))
            )}
          </TableBody>
        </StyledTable>
      </TableContainer>
    </ScheduleProvider>
  );
};

export default ScheduleView;
